import { t } from "../../hooks/useAppTranslation";
import React, { useRef, useState } from "react";
import * as Yup from "yup";
import Input from "../Input/Input";
import SelectCustom from "../SelectCustom/SelectCustom";
import { ICustomSelectValues, IDictionary } from "./ExternalControlContainer";
import styles from "./ExternalControlStyle.module.scss";
import {ISelectSearchProps} from "./ExternalControlForm";
import { formatCurrency } from "../ControlStateHelper";

export interface IConseqeunce {
  type: string;
  typeId?: number;
  lawBase: string;
  contractNumber: string;
  correctionAccural: number;
  correctionPercent: number;
  correctionValue: string;
}

type Props = {
  conseqeuncesTypes: IDictionary[];
  consequencesPercents: IDictionary[];
  consequences: IConseqeunce[];
  setFieldValue(args: string, val: any): void;
};
export const ConsequencesFormHelper: React.FC<Props> = ({
  conseqeuncesTypes,
  consequencesPercents,
  consequences,
  setFieldValue,
}) => {
  let conseqeuncesSchema = Yup.object({
    type: Yup.string().required(t('external-controls-correction-type-required')),
    lawBase: Yup.string().required(t('external-controls-legal-basis-required')),
    contractNumber: Yup.string().required(t('external-controls-contract-number-required')),
    correctionAccural: Yup.string().when("typeId", (typeId, schema) => {
      if (typeId === 1) {
        return schema.required(t('external-controls-basis-calculating-correction-required'));
      }
      return schema;
    }),
    correctionPercent: Yup.string().when("typeId", (typeId, schema) => {
      if (typeId === 1) {
        return schema.required(t('external-controls-correction-rate-required'));
      }
      return schema;
    }),
    correctionValue: Yup.string().required(
      t('external-controls-financial-correction-required')
    ),
  });
  const [type, setType] = useState<ICustomSelectValues>();
  const [correctionPercent, setCorrectionPercent] = useState("");

  const [lawBase, setLawBase] = useState("");
  const [contractNumber, setContractNumber] = useState("");
  const [correctionAccural, setCorrectionAccural] = useState("");
  const [correctionValue, setCorrectionValue] = useState("");
  const [error, setError] = useState("");

  const [previousSearch, setPreviousSearch] = useState('');
  const [previousSearchStawka, setPreviousSearchStawka] = useState('');

  const selectCustomRef1 = useRef(null);
  const selectCustomRef2 = useRef(null);

  const stringToFloat = (string: string | undefined): number => {
    if (!string) return 0;
    
    if (string.includes(',')) {
      return parseFloat(string.replaceAll('.', '').replaceAll(',', '.'));
    }
    
    return parseFloat(string.replace(/\s+/g, ''));
  };

  const calculateCorrectionValue = (correctionAccural: string | undefined, correctionPercent:  string | undefined): void => {
    const a = stringToFloat(correctionAccural);
    const b = stringToFloat(correctionPercent);
    const value = (a ?? 0) * ((b ?? 0) * 0.01);
    setCorrectionValue(formatCurrency(value, 2))
  }

  const onTypeChange = (value: ICustomSelectValues): void => {
    setType(value);
    if (!isIndicatorType(value?.value)) {
      setCorrectionAccural("");
      setCorrectionPercent("");
    }
  }

  const isIndicatorType = (value: number | undefined): boolean => value === 1;

  const isValidPercentageInput = (value: string): boolean => {
    if (!value) return true; // Empty string is valid
    
    // Check if contains only valid characters (digits, comma, dot)
    if (!/^[0-9,.]*$/.test(value)) return false;
    
    // Reject entries containing only separators
    if (/^[,.]+$/.test(value)) return false;
    
    // Reject entries starting with separator
    if (/^[,.]/.test(value)) return false;
    
    // Count decimal separators
    const commaCount = (value.match(/,/g) || []).length;
    const dotCount = (value.match(/\./g) || []).length;
    
    // Only one decimal separator allowed
    if (commaCount + dotCount > 1) return false;
    
    // Multiple consecutive separators not allowed
    if (/[,.]{2,}/.test(value)) return false;
    
    // Check numeric(19,2) constraints
    const separatorIndex = Math.max(value.indexOf(','), value.indexOf('.'));
    
    if (separatorIndex === -1) {
      // No decimal separator - check if integer part exceeds 17 digits
      if (value.length > 17) return false;
    } else {
      // Has decimal separator
      const integerPart = value.substring(0, separatorIndex);
      const decimalPart = value.substring(separatorIndex + 1);
      
      // Check decimal places limit (max 2 digits after separator)
      if (decimalPart.length > 2) return false;
      
      // Check integer part limit (max 17 digits before separator)
      if (integerPart.length > 17) return false;
      
      // Check total digits limit (max 19)
      if (integerPart.length + decimalPart.length > 19) return false;
    }
    
    return true;
  };

  const isNumberValue = (value: string): boolean => value?.split('')?.every((ch: any) => '1234567890,.'.includes(ch));

  return (
    <>
      <div className='row'>
        <div className='col-md-5'>
          <SelectCustom
            id='ext-cnt-consType'
            label={t('external-controls-correction-type')}
            placeholder={t('external-controls-correction-type')}
            name='type'
            onChange={(value: ICustomSelectValues[]) => onTypeChange(value[0])}
            options={conseqeuncesTypes.map((el) => {
              return { label: el.description, value: el.entryId };
            })}
            values={
              type
                ? [
                    {
                      label: type.label,
                      value: type.value,
                    },
                  ]
                : []
            }
            searchFn={(selectProps:ISelectSearchProps) => {
              const options = selectProps.props?.options;
              const includesOptions = options?.find((el: ICustomSelectValues) => el.label.toUpperCase().includes(selectProps.state.search.toUpperCase()));
              (includesOptions) ? setPreviousSearch(selectProps.state.search) : selectProps.state['search'] = previousSearch;
            }}
            keepSelectedInList={true}
            color='#0d47a1'
            containerClassName={styles.selectContainer}
            labelClassName={styles.selectCustomLabel}
            error=''
            errorId=''
            invalid={false}
            labelHeader={false}
            isMulti={false}
            onClear=''
            dropdownId=''
            disabled={false}
            parentRef={selectCustomRef1}
            className=''
            optionNameVisible={undefined}
          />
        </div>
        <div className='col-md-7'>
          <Input
            inputId='ext-cnt-lawBase'
            label={t('external-controls-legal-basis-infringement')}
            placeholder={t('external-controls-description')}
            type='text'
            formik={true}
            maxLength={2000}
            value={lawBase}
            chartsCount={lawBase.length}
            onChange={(e) => setLawBase(e.target.value)}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-md-3'>
          <Input
            inputId='ext-cnt-cons-correctNumber'
            label={t('external-controls-contract-number')}
            placeholder={t('external-controls-contract-number')}
            type='text'
            maxLength={50}
            chartsCount={contractNumber.length}
            formik={true}
            value={contractNumber}
            onChange={(e) => setContractNumber(e.target.value)}
          />
        </div>
        <div className='col-md-3'>
          <Input
            inputId='ext-cnt-cons-correctAccural'
            label={t('external-controls-basis-calculating-correction')}
            placeholder={t('external-controls-basis-calculating-correction')}
            type='number'
            maxLength={50}
            chartsCount={correctionAccural?.length}
            formik={true}
            value={correctionAccural}
            onChange={(e) => {if (isNumberValue(e.target.value)) {
              setCorrectionAccural(e.target.value); calculateCorrectionValue(e.target.value, correctionPercent);
            }}}
            disabled={!isIndicatorType(type?.value)}
            onKeyPress={(event) => {
              if (!/[0-9_,_.]/.test(event.key)) {
                event.preventDefault();
              }
            }}
            min={0}
            step={.01}
          />
        </div>
        <div className='col-md-2'>
          <Input
            inputId='ext-cnt-cons-correctPercent'
            label={t('external-controls-correction-rate')}
            placeholder={t('external-controls-rate')}
            onChange={(e) => {
              if (isValidPercentageInput(e.target.value)) {
                setCorrectionPercent(e.target.value);
                calculateCorrectionValue(correctionAccural, e.target.value);
              }
            }}
            color='#0d47a1'
            containerClassName={styles.selectContainer}
            labelClassName={styles.selectCustomLabel}
            type='number'
            disabled={!isIndicatorType(type?.value)}
            min={0}
            step={.01}
            onKeyPress={(event) => {
              const currentValue = correctionPercent;
              
              // Allow only digits, comma, and dot
              if (!/[0-9,.]/.test(event.key)) {
                event.preventDefault();
                return;
              }
              
              // Prevent multiple decimal separators
              if ((event.key === ',' || event.key === '.') && (currentValue.includes(',') || currentValue.includes('.'))) {
                event.preventDefault();
                return;
              }
              
              // Prevent starting with separator
              if ((event.key === ',' || event.key === '.') && currentValue === '') {
                event.preventDefault();
                return;
              }
            }}
            value={correctionPercent}
          />
        </div>

        <div className='col-md-4'>
          <Input
            inputId='ext-cnt-cons-correctValue'
            label={t('external-controls-value-financial-correction')}
            placeholder={t('external-controls-value-financial-correction')}
            type='text'
            maxLength={50}
            chartsCount={correctionValue.length}
            formik={true}
            value={correctionValue}
            onChange={(e) => {isNumberValue(e.target.value) && setCorrectionValue(e.target.value)}}
            onKeyPress={(event) => {
              if (!/[0-9_,_.]/.test(event.key)) {
                event.preventDefault();
              }
            }}
          />
        </div>
      </div>

      <div className='row'>
        <div className='col-md-4'>
          <button
            className='btn save-btn uppercase'
            type='button'
            onClick={async () => {
              setError("");
              const isNotUnique = consequences.find(
                (el: IConseqeunce) => el.contractNumber === contractNumber
              );
              const consObj = {
                typeId: type?.value,
                type: type?.label,
                lawBase,
                contractNumber,
                correctionAccural,
                correctionPercent,
                correctionValue,
              };
              await conseqeuncesSchema
                .validate(consObj)
                .then(() => {
                  if (!isNotUnique) {
                    setFieldValue("consequences", [
                      ...consequences,
                      {
                          ...consObj,
                          correctionAccural: stringToFloat(correctionAccural),
                          correctionPercent: stringToFloat(correctionPercent),
                      },
                  ]);
                  } else setError(t('external-controls-contract-number-cannot-repeated'));
                })
                .catch((err) => setError(err.message));
            }}
          >
            {t('g-add')}
          </button>
        </div>
      </div>
      <div className='row'>
        <div className='col-md-12'>
          {error && <p className={styles.errorMessage}>{error}</p>}
        </div>
      </div>

      {consequences.map((consequence: IConseqeunce) => {
        return (
          <div key={consequence.contractNumber} className={styles.listSpacing}>
            <div className='row' key={consequence.contractNumber}>
              <div className='col-md-10'>
                <p>
                  {consequence.contractNumber} - {consequence.correctionAccural ? `${consequence.correctionAccural} - ` : ''}{consequence.correctionPercent ? `${consequence.correctionPercent}% - ` : ''}{consequence.correctionValue}
                </p>
              </div>
              <div className='col-md-2'>
                <button
                  className='btn cancel-btn uppercase'
                  onClick={() => {
                    const updatedValues = consequences.filter(
                      (el: IConseqeunce) =>
                        el.contractNumber !== consequence.contractNumber
                    );
                    setFieldValue("consequences", updatedValues);
                  }}
                >
                  {t('g-delete')}
                  <span className='sr-only'>
                    {consequence.contractNumber} -{" "}
                    {consequence.correctionAccural} -{" "}
                    {consequence.correctionPercent}
                    {"%"}- {consequence.correctionValue}
                  </span>
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};
